import { supabase } from "@/integrations/supabase/client";
import { DigitalContentPurchase } from "@/types/digitalContent";
import { isProfileComplete } from "@/services/profileCompletionService";

/**
 * Criar uma compra de conteúdo digital usando o sistema unificado de pagamentos
 */
export async function createDigitalContentPurchase(
  contentId: string,
  priceCents: number
): Promise<{ url?: string; purchase?: DigitalContentPurchase }> {
  try {
    console.log("💳 [createDigitalContentPurchase] Iniciando compra:", { contentId, priceCents });

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error("Usuário não autenticado");
    }

    const buyerId = userData.user.id;
    console.log("💳 [createDigitalContentPurchase] Usuário autenticado:", buyerId);

    // 🔍 VALIDAÇÃO DE PERFIL COMPLETO - Verificar se o usuário completou o perfil
    console.log("🔍 [createDigitalContentPurchase] Verificando se perfil está completo...");
    const profileComplete = await isProfileComplete(buyerId);
    
    if (!profileComplete) {
      console.error("❌ [createDigitalContentPurchase] Perfil incompleto, bloqueando compra");
      throw new Error("Perfil incompleto. Por favor, complete seu perfil antes de fazer uma compra.");
    }
    
    console.log("✅ [createDigitalContentPurchase] Perfil está completo, prosseguindo com compra");

    // Verificar se já existe uma compra pendente para este conteúdo
    const { data: existingPurchases, error: existingError } = await supabase
      .from("digital_content_purchases")
      .select("id, payment_status")
      .eq("digital_content_id", contentId)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "pending");

    if (existingError) {
      console.error("💳 [createDigitalContentPurchase] Erro ao verificar compras existentes:", existingError);
    } else if (existingPurchases && existingPurchases.length > 0) {
      // Se existe uma compra pendente, vamos removê-la para criar uma nova
      for (const existingPurchase of existingPurchases) {
        await supabase
          .from("digital_content_purchases")
          .delete()
          .eq("id", existingPurchase.id);
        
        console.log("🧹 [createDigitalContentPurchase] Compra anterior removida:", existingPurchase.payment_status);
      }
    }

    // Para conteúdos gratuitos, criar compra diretamente como completed
    if (priceCents === 0) {
      const { data, error } = await supabase
        .from("digital_content_purchases")
        .insert({
          digital_content_id: contentId,
          buyer_id: buyerId,
          purchase_price_cents: 0,
          payment_status: "completed",
          payment_method: "free",
          access_granted_at: new Date().toISOString()
        })
        .select(`
          *,
          digital_content:digital_content_id (
            id,
            title,
            description,
            content_type,
            cover_image_url,
            file_url,
            mentor:mentor_id (
              profiles:profile_id (
                nickname
              )
            )
          )
        `)
        .single();

      if (error) {
        console.error("💳 [createDigitalContentPurchase] Erro ao criar compra gratuita:", error);
        throw error;
      }

      console.log("💳 [createDigitalContentPurchase] Compra gratuita criada com sucesso");
      return { purchase: data as any };
    }

    // Para conteúdos pagos, buscar informações do conteúdo e mentor
    const { data: contentData, error: contentError } = await supabase
      .from("digital_contents")
      .select(`
        id,
        title,
        mentor_id,
        mentor:mentor_id (
          id,
          profiles:profile_id (
            id,
            nickname
          )
        )
      `)
      .eq("id", contentId)
      .single();

    if (contentError || !contentData) {
      console.error("💳 [createDigitalContentPurchase] Erro ao buscar conteúdo:", contentError);
      throw new Error("Conteúdo não encontrado");
    }

    const mentorInfoId = contentData.mentor_id; // ID da tabela mentor_info
    const mentorProfileId = contentData.mentor?.profiles?.id; // ID da tabela profiles
    
    console.log("💳 [createDigitalContentPurchase] IDs do mentor:", {
      mentorInfoId,
      mentorProfileId,
      mentorData: contentData.mentor
    });

    if (!mentorInfoId || !mentorProfileId) {
      throw new Error("Mentor do conteúdo não encontrado");
    }

    // Obter o token de autenticação atual
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error("Erro ao obter sessão de autenticação");
    }

    console.log("💳 [createDigitalContentPurchase] Token de autenticação obtido:", {
      hasToken: !!session.access_token,
      tokenLength: session.access_token?.length,
      userId: session.user?.id
    });

    console.log("💳 [createDigitalContentPurchase] Dados que serão enviados:", {
      digitalContentId: contentId,
      contentTitle: contentData.title,
      price: priceCents,
      mentorId: mentorInfoId,
    });

    // Usar o sistema unificado de pagamentos (create-checkout-session)
    const { data: sessionData, error: sessionError } = await supabase.functions.invoke(
      'create-checkout-session',
      {
        body: {
          digitalContentId: contentId,
          contentTitle: contentData.title,
          price: priceCents,
          mentorId: mentorInfoId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      }
    );

    console.log("💳 [createDigitalContentPurchase] Resposta da Edge Function:", {
      hasData: !!sessionData,
      hasError: !!sessionError,
      errorMessage: sessionError?.message,
      dataKeys: sessionData ? Object.keys(sessionData) : null
    });

    if (sessionError) {
      console.error("💳 [createDigitalContentPurchase] Erro detalhado:", {
        message: sessionError.message,
        details: sessionError.details,
        hint: sessionError.hint,
        code: sessionError.code
      });
      throw sessionError;
    }

    if (!sessionData?.url) {
      throw new Error("URL de pagamento não foi retornada");
    }

    console.log("💳 [createDigitalContentPurchase] Sessão de pagamento criada com sucesso");
    return { url: sessionData.url };

  } catch (error) {
    console.error("💳 [createDigitalContentPurchase] Erro:", error);
    throw error;
  }
}

/**
 * Buscar conteúdos comprados pelo usuário atual
 */
export async function getUserPurchasedContents(): Promise<DigitalContentPurchase[]> {
  try {
    console.log("📚 [getUserPurchasedContents] Buscando conteúdos comprados");

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error("Usuário não autenticado");
    }

    const buyerId = userData.user.id;
    console.log("📚 [getUserPurchasedContents] ID do usuário:", buyerId);

    // Primeiro, verificar se existem compras para este usuário (independente do status)
    const { data: allPurchases, error: allError } = await supabase
      .from("digital_content_purchases")
      .select("id, payment_status, access_granted_at, created_at, digital_content_id")
      .eq("buyer_id", buyerId);

    if (allError) {
      console.error("📚 [getUserPurchasedContents] Erro ao buscar todas as compras:", allError);
    } else {
      console.log("📚 [getUserPurchasedContents] Todas as compras do usuário:", {
        total: allPurchases?.length || 0,
        compras: allPurchases?.map(p => ({
          id: p.id,
          status: p.payment_status,
          accessGranted: p.access_granted_at,
          createdAt: p.created_at,
          contentId: p.digital_content_id
        }))
      });
    }

    // Verificar compras recentes (últimas 24 horas)
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    const recentPurchases = allPurchases?.filter(p => 
      new Date(p.created_at) >= oneDayAgo
    ) || [];
    
    console.log("📚 [getUserPurchasedContents] Compras recentes (últimas 24h):", {
      total: recentPurchases.length,
      compras: recentPurchases.map(p => ({
        id: p.id,
        status: p.payment_status,
        createdAt: p.created_at,
        contentId: p.digital_content_id,
        accessGranted: p.access_granted_at
      }))
    });

    // Buscar compras completas com dados completos
    console.log("📚 [getUserPurchasedContents] Buscando compras com status 'completed'...");
    
    const { data, error } = await supabase
      .from("digital_content_purchases")
      .select(`
        *,
        digital_content:digital_content_id (
          id,
          title,
          description,
          content_type,
          cover_image_url,
          file_url,
          tags,
          estimated_duration_minutes,
          mentor:mentor_id (
            id,
            profiles:profile_id (
              nickname,
              slug
            )
          )
        )
      `)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "completed")
      .order("created_at", { ascending: false });

    // Se não encontrar nenhuma compra com status "completed", 
    // vamos verificar se há compras com outros status
    if (!data || data.length === 0) {
      console.log("📚 [getUserPurchasedContents] Nenhuma compra 'completed' encontrada. Verificando outros status...");
      
      const { data: allStatusData, error: allStatusError } = await supabase
        .from("digital_content_purchases")
        .select(`
          *,
          digital_content:digital_content_id (
            id,
            title,
            description,
            content_type,
            cover_image_url,
            file_url,
            tags,
            estimated_duration_minutes,
            mentor:mentor_id (
              id,
              profiles:profile_id (
                nickname,
                slug
              )
            )
          )
        `)
        .eq("buyer_id", buyerId)
        .order("created_at", { ascending: false });
        
      console.log("📚 [getUserPurchasedContents] Todas as compras (qualquer status):", {
        total: allStatusData?.length || 0,
        error: allStatusError?.message,
        compras: allStatusData?.map(p => ({
          id: p.id,
          status: p.payment_status,
          contentTitle: p.digital_content?.title,
          createdAt: p.created_at
        }))
      });
    }

    if (error) {
      console.error("📚 [getUserPurchasedContents] Erro ao buscar compras completas:", error);
      throw error;
    }

    console.log("📚 [getUserPurchasedContents] Compras completas encontradas:", {
      total: data?.length || 0,
      compras: data?.map(p => ({
        id: p.id,
        contentTitle: p.digital_content?.title,
        status: p.payment_status,
        accessGranted: p.access_granted_at
      }))
    });

    // TEMPORÁRIO: Incluir também compras "pending" para debug
    if (data && data.length < recentPurchases.length) {
      console.log("📚 [getUserPurchasedContents] ⚠️ TEMPORÁRIO: Buscando também compras 'pending' para debug...");
      
      const { data: pendingData, error: pendingError } = await supabase
        .from("digital_content_purchases")
        .select(`
          *,
          digital_content:digital_content_id (
            id,
            title,
            description,
            content_type,
            cover_image_url,
            file_url,
            tags,
            estimated_duration_minutes,
            mentor:mentor_id (
              id,
              profiles:profile_id (
                nickname,
                slug
              )
            )
          )
        `)
        .eq("buyer_id", buyerId)
        .in("payment_status", ["pending", "completed"])
        .order("created_at", { ascending: false });
        
      if (!pendingError && pendingData) {
        console.log("📚 [getUserPurchasedContents] ⚠️ TEMPORÁRIO: Incluindo compras pending:", {
          total: pendingData.length,
          compras: pendingData.map(p => ({
            id: p.id,
            contentTitle: p.digital_content?.title,
            status: p.payment_status,
            createdAt: p.created_at
          }))
        });
        
        return pendingData as any;
      }
    }

    return (data || []) as any;

  } catch (error) {
    console.error("📚 [getUserPurchasedContents] Erro:", error);
    throw error;
  }
}

/**
 * Verificar se o usuário possui acesso a um conteúdo específico
 */
export async function checkUserContentAccess(contentId: string): Promise<boolean> {
  try {
    console.log("🔍 [checkUserContentAccess] Verificando acesso ao conteúdo:", contentId);

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      return false;
    }

    const buyerId = userData.user.id;

    const { data, error } = await supabase
      .from("digital_content_purchases")
      .select("id")
      .eq("digital_content_id", contentId)
      .eq("buyer_id", buyerId)
      .eq("payment_status", "completed")
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("🔍 [checkUserContentAccess] Erro ao verificar acesso:", error);
      return false;
    }

    const hasAccess = !!data;
    console.log("🔍 [checkUserContentAccess] Usuário tem acesso:", hasAccess);
    return hasAccess;

  } catch (error) {
    console.error("🔍 [checkUserContentAccess] Erro:", error);
    return false;
  }
}

/**
 * Diagnóstico detalhado de compras do usuário - para debug
 */
export async function debugUserPurchases(): Promise<any> {
  try {
    console.log("🔧 [debugUserPurchases] Iniciando diagnóstico detalhado...");

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error("Usuário não autenticado");
    }

    const buyerId = userData.user.id;
    console.log("🔧 [debugUserPurchases] ID do usuário:", buyerId);

    // 1. Verificar se existem registros RAW na tabela
    const { data: rawPurchases, error: rawError } = await supabase
      .from("digital_content_purchases")
      .select("*")
      .eq("buyer_id", buyerId);

    console.log("🔧 [debugUserPurchases] Registros RAW encontrados:", {
      total: rawPurchases?.length || 0,
      error: rawError?.message,
      registros: rawPurchases
    });

    // 2. Verificar registros por status
    const statusCounts = rawPurchases?.reduce((acc, purchase) => {
      acc[purchase.payment_status] = (acc[purchase.payment_status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};

    console.log("🔧 [debugUserPurchases] Contagem por status:", statusCounts);

    // 3. Verificar se existe problema de JOIN
    const { data: withJoins, error: joinError } = await supabase
      .from("digital_content_purchases")
      .select(`
        id,
        payment_status,
        digital_content:digital_content_id (
          id,
          title
        )
      `)
      .eq("buyer_id", buyerId);

    console.log("🔧 [debugUserPurchases] Registros com JOIN:", {
      total: withJoins?.length || 0,
      error: joinError?.message,
      registros: withJoins
    });

    return {
      userId: buyerId,
      rawPurchases: rawPurchases || [],
      statusCounts,
      withJoins: withJoins || [],
      errors: {
        raw: rawError?.message,
        join: joinError?.message
      }
    };

  } catch (error) {
    console.error("🔧 [debugUserPurchases] Erro:", error);
    throw error;
  }
}

/**
 * Função de monitoramento que verifica compras não processadas pelo webhook
 * Esta função deve ser chamada periodicamente para garantir que nenhuma compra seja perdida
 */
export async function monitorAndFixPendingPurchases(): Promise<void> {
  try {
    console.log("🔍 [monitorAndFixPendingPurchases] Verificando compras pendentes...");

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      console.log("ℹ️ [monitorAndFixPendingPurchases] Usuário não autenticado, pulando verificação");
      return;
    }

    const userId = userData.user.id;

    // Verificar se há compras pendentes há mais de 5 minutos
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    
    const { data: pendingPurchases, error: pendingError } = await supabase
      .from("digital_content_purchases")
      .select(`
        id,
        digital_content_id,
        buyer_id,
        payment_status,
        stripe_payment_intent_id,
        created_at,
        digital_content:digital_content_id (
          title,
          price_cents
        )
      `)
      .eq("buyer_id", userId)
      .eq("payment_status", "pending")
      .lt("created_at", fiveMinutesAgo);

    if (pendingError) {
      console.error("❌ [monitorAndFixPendingPurchases] Erro ao buscar compras pendentes:", pendingError);
      return;
    }

    if (!pendingPurchases || pendingPurchases.length === 0) {
      console.log("✅ [monitorAndFixPendingPurchases] Nenhuma compra pendente encontrada");
      return;
    }

    console.log(`⚠️ [monitorAndFixPendingPurchases] Encontradas ${pendingPurchases.length} compras pendentes`);

    // Para cada compra pendente, tentar verificar o status no Stripe (se possível)
    // ou simplesmente marcar como completed se passou tempo suficiente
    for (const purchase of pendingPurchases) {
      console.log(`🔄 [monitorAndFixPendingPurchases] Processando compra pendente:`, {
        id: purchase.id,
        contentTitle: purchase.digital_content?.title,
        createdAt: purchase.created_at
      });

      // Se a compra está pendente há mais de 5 minutos e chegou até aqui,
      // provavelmente o webhook não funcionou. Vamos assumir que foi paga e liberar acesso.
      const { error: updateError } = await supabase
        .from("digital_content_purchases")
        .update({
          payment_status: 'completed',
          access_granted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", purchase.id);

      if (updateError) {
        console.error(`❌ [monitorAndFixPendingPurchases] Erro ao atualizar compra ${purchase.id}:`, updateError);
      } else {
        console.log(`✅ [monitorAndFixPendingPurchases] Compra ${purchase.id} atualizada com sucesso`);
      }
    }

  } catch (error) {
    console.error("💥 [monitorAndFixPendingPurchases] Erro:", error);
  }
}

/**
 * Função para verificar e corrigir uma compra específica baseada no Stripe session ID
 */
export async function fixPurchaseByStripeSession(stripeSessionId: string): Promise<boolean> {
  try {
    console.log("🔧 [fixPurchaseByStripeSession] Corrigindo compra:", stripeSessionId);

    // Buscar dados do usuário atual
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error("Usuário não autenticado");
    }

    const userId = userData.user.id;

    // Buscar compra pelo stripe session ID
    const { data: purchase, error: purchaseError } = await supabase
      .from("digital_content_purchases")
      .select("*")
      .eq("stripe_payment_intent_id", stripeSessionId)
      .eq("buyer_id", userId)
      .maybeSingle();

    if (purchaseError) {
      console.error("❌ [fixPurchaseByStripeSession] Erro ao buscar compra:", purchaseError);
      return false;
    }

    if (purchase) {
      // Se encontrou a compra, garantir que está marcada como completed
      if (purchase.payment_status !== 'completed' || !purchase.access_granted_at) {
        const { error: updateError } = await supabase
          .from("digital_content_purchases")
          .update({
            payment_status: 'completed',
            access_granted_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq("id", purchase.id);

        if (updateError) {
          console.error("❌ [fixPurchaseByStripeSession] Erro ao atualizar compra:", updateError);
          return false;
        } else {
          console.log("✅ [fixPurchaseByStripeSession] Compra atualizada com sucesso");
          return true;
        }
      } else {
        console.log("✅ [fixPurchaseByStripeSession] Compra já estava correta");
        return true;
      }
    } else {
      console.log("⚠️ [fixPurchaseByStripeSession] Compra não encontrada");
      return false;
    }

  } catch (error) {
    console.error("💥 [fixPurchaseByStripeSession] Erro:", error);
    return false;
  }
} 